import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useQCM } from '../../contexts/QCMContext';
import { getCertaintyCoeff, getValidationThresholdForExam } from '../../config/scoringConfig';
import { useTranslation } from 'react-i18next';
import QCMOptionToggle from './QCMOptionToggle';
import SectionContent from '../lab/SectionContent';

const ExamQCMOptions = () => {
    const { t } = useTranslation();
    const {
        currentQCM,
        selectedOption,
        setSelectedOption,
        degreeOfCertainty,
        setDegreeOfCertainty,
        submitQCMAnswer,
        loading,
        setHoveredCertainty,
        attemptCount,
        elapsedTime,
        maxTime,
        setCurrentQCM,
        setDisplayMode,
        sessionContext
    } = useQCM();

    const validationThreshold = getValidationThresholdForExam(sessionContext?.exam);

    const [hoveredOption, setHoveredOption] = useState(null);
    const [markedIncorrect, setMarkedIncorrect] = useState(new Set());
    const [selectedAnswers, setSelectedAnswers] = useState(new Set());
    const [selectedCertainties, setSelectedCertainties] = useState(new Map()); // Map<answer, certainty>
    const [rawScore, setRawScore] = useState(null);
    const [isEvaluated, setIsEvaluated] = useState(false);
    const [weakPoints, setWeakPoints] = useState([]);

    // Reset local states when currentQCM changes
    React.useEffect(() => {
        setMarkedIncorrect(new Set());
        setSelectedAnswers(new Set());
        setSelectedCertainties(new Map());
        setRawScore(null);
        setIsEvaluated(false);
        setWeakPoints([]);
    }, [currentQCM?.qcm_id]);

    // Mélanger les options pour randomiser la position de la bonne réponse
    // Gérer les réponses correctes (simple ou multiples)
    const correctAnswers = useMemo(() => {
        if (!currentQCM || !currentQCM.qcm) return [];
        
        console.log('🔍 ExamQCM Debug - correct_answer:', currentQCM.qcm.correct_answer);
        console.log('🔍 ExamQCM Debug - is array:', Array.isArray(currentQCM.qcm.correct_answer));
        
        const normalize = (opt) => {
            if (!opt) return null;
            if (typeof opt === 'string') return { answer: opt };
            if (opt && typeof opt.answer === 'string') return opt;
            return null;
        };
        
        let result = [];
        
        // Format MongoDB : correct_answer peut être un array ou un objet simple
        if (Array.isArray(currentQCM.qcm.correct_answer)) {
            // Format array : [{ answer: "...", explanation: "..." }, ...]
            result = currentQCM.qcm.correct_answer.map(normalize).filter(Boolean);
        } else if (currentQCM.qcm.correct_answer) {
            // Format objet simple : { answer: "...", explanation: "..." }
            result = [normalize(currentQCM.qcm.correct_answer)].filter(Boolean);
        }
        
        // Fallback pour correct_answers (au pluriel)
        if (result.length === 0 && Array.isArray(currentQCM.qcm.correct_answers)) {
            result = currentQCM.qcm.correct_answers.map(normalize).filter(Boolean);
        }
        
        console.log('🔍 ExamQCM Debug - correctAnswers result:', result);
        return result;
    }, [currentQCM?.qcm?.correct_answer, currentQCM?.qcm?.correct_answers]);

    const allOptions = useMemo(() => {
        if (!currentQCM || !currentQCM.qcm) {
            return [];
        }
        
        const options = [
            ...correctAnswers,
            ...(currentQCM.qcm.distractors || [])
        ];
        
        // Mélange Fisher-Yates pour un mélange vraiment aléatoire
        const shuffledOptions = [...options];
        for (let i = shuffledOptions.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffledOptions[i], shuffledOptions[j]] = [shuffledOptions[j], shuffledOptions[i]];
        }
        
        console.log('🎲 ExamQCM Options mélangées:', shuffledOptions.map(opt => opt.answer));
        return shuffledOptions;
    }, [currentQCM?.qcm?.correct_answer, currentQCM?.qcm?.distractors]);

    // Calculate raw score when selected answers change
    const calculateRawScore = useCallback((selected, correct, attemptCount, elapsedTime, expectedTime, certainty) => {
        if (selected.size === 0 || correct.length === 0) return null;
        
        console.log('🔍 calculateRawScore Debug:', {
            selected: [...selected],
            correct: correct.map(ca => ca.answer),
            attemptCount,
            elapsedTime,
            expectedTime,
            certainty
        });
        
        const correctAnswersSet = new Set(correct.map(ca => ca.answer));
        const correctSelected = [...selected].filter(answer => correctAnswersSet.has(answer));
        
        // Base score: nombre de bonnes réponses / nombre de bonnes réponses attendues
        const baseScore = correctSelected.length / correct.length;
        
        // Coefficients selon la spécification
        // attempt_coeff = min(1/(nb_attempt-0.5), 1)
        // Assurer que attemptCount est au moins 1
        const safeAttemptCount = Math.max(attemptCount || 1, 1);
        const attemptCoeff = Math.min(1 / (safeAttemptCount - 0.5), 1);
        
        // duration_coeff = min(expected_time/(elapsed_time+expected_time/10), 1)
        const durationCoeff = expectedTime > 0 
            ? Math.min(expectedTime / (elapsedTime + expectedTime / 10), 1)
            : 1;
        
        let certaintyCoeff = 1;
        if (Array.isArray(certainty)) {
            const certaintyValues = certainty.map((c) => getCertaintyCoeff(c));
            certaintyCoeff = certaintyValues.reduce((sum, val) => sum + val, 0) / certaintyValues.length;
        } else if (certainty != null && certainty !== '') {
            certaintyCoeff = getCertaintyCoeff(certainty);
        }
        
        const rawScore = Math.floor(baseScore * attemptCoeff * durationCoeff * 10);
        
        // Score pondéré (pour l'instant identique au raw score)
        const weightedScore = rawScore;
        
        console.log('🔍 Score calculation result:', {
            baseScore,
            attemptCoeff,
            durationCoeff,
            certaintyCoeff,
            rawScore
        });
        
        return {
            raw: rawScore,
            weighted: weightedScore,
            correctSelected: correctSelected.length,
            totalCorrect: correct.length,
            incorrectSelected: selected.size - correctSelected.length,
            // Détails des coefficients pour debug
            coefficients: {
                baseScore,
                attemptCoeff,
                durationCoeff,
                certaintyCoeff,
                final: baseScore * attemptCoeff * durationCoeff
            }
        };
    }, []);

    // Auto-evaluation when number of selected answers equals number of correct answers
    useEffect(() => {
        if (selectedAnswers.size === correctAnswers.length && correctAnswers.length > 0) {
            // Debug des valeurs du contexte
            console.log('🔍 Context values debug:', {
                attemptCount,
                elapsedTime,
                maxTime,
                degreeOfCertainty,
                selectedAnswers: [...selectedAnswers],
                selectedCertainties: Object.fromEntries(selectedCertainties)
            });
            
            // Récupérer les certainties pour les réponses sélectionnées
            const certainties = [...selectedAnswers].map(answer => selectedCertainties.get(answer) || degreeOfCertainty);
            
            const score = calculateRawScore(
                selectedAnswers, 
                correctAnswers, 
                attemptCount, 
                elapsedTime, 
                maxTime, 
                certainties
            );
            setRawScore(score);
            setIsEvaluated(true);
            
            // Generate weak points based on incorrect selections
            const correctAnswersSet = new Set(correctAnswers.map(ca => ca.answer));
            const incorrectSelected = [...selectedAnswers].filter(answer => !correctAnswersSet.has(answer));
            setWeakPoints(incorrectSelected);
            
            // Reset incorrect options (mauvaises options)
            setMarkedIncorrect(new Set());
            
            // Auto-submit for scoring - only if answers are correct
            const allSelectedCorrect = [...selectedAnswers].every(answer => correctAnswersSet.has(answer));
            const allCorrectSelected = [...correctAnswersSet].every(answer => selectedAnswers.has(answer));
            
            if (allSelectedCorrect && allCorrectSelected) {
                // Only submit if all selected answers are correct and all correct answers are selected
                setTimeout(() => {
                    submitQCMAnswer([...selectedAnswers], degreeOfCertainty);
                }, 100);
            }
        }
    }, [selectedAnswers, correctAnswers, calculateRawScore, submitQCMAnswer, degreeOfCertainty, attemptCount, elapsedTime, maxTime, selectedCertainties]);

    // Handle single correct answer case
    useEffect(() => {
        if (correctAnswers.length === 1 && selectedAnswers.size === 1) {
            // Récupérer la certitude pour la réponse sélectionnée
            const answer = [...selectedAnswers][0];
            const certainty = selectedCertainties.get(answer) || degreeOfCertainty;
            
            const score = calculateRawScore(
                selectedAnswers, 
                correctAnswers, 
                attemptCount, 
                elapsedTime, 
                maxTime, 
                certainty
            );
            setRawScore(score);
            setIsEvaluated(true);
            
            // Generate weak points based on incorrect selections
            const correctAnswersSet = new Set(correctAnswers.map(ca => ca.answer));
            const incorrectSelected = [...selectedAnswers].filter(answer => !correctAnswersSet.has(answer));
            setWeakPoints(incorrectSelected);
            
            // Reset incorrect options (mauvaises options)
            setMarkedIncorrect(new Set());
            
            // Auto-submit for scoring - only if answers are correct
            const allSelectedCorrect = [...selectedAnswers].every(answer => correctAnswersSet.has(answer));
            const allCorrectSelected = [...correctAnswersSet].every(answer => selectedAnswers.has(answer));
            
            if (allSelectedCorrect && allCorrectSelected) {
                // Only submit if all selected answers are correct and all correct answers are selected
                setTimeout(() => {
                    submitQCMAnswer([...selectedAnswers], degreeOfCertainty);
                }, 100);
            }
        }
    }, [selectedAnswers, correctAnswers, calculateRawScore, submitQCMAnswer, degreeOfCertainty, attemptCount, elapsedTime, maxTime, selectedCertainties]);

    // Emit selection data for InfoPanel
    useEffect(() => {
        console.log('📡 ExamQCMOptions emitting selection update:', {
            selectedAnswers: [...selectedAnswers],
            selectedAnswersSize: selectedAnswers.size,
            selectedCertainties: Object.fromEntries(selectedCertainties),
            selectedCertaintiesSize: selectedCertainties.size,
            correctAnswers: correctAnswers,
            correctAnswersLength: correctAnswers.length
        });
        
        // Émettre même si aucune sélection pour initialiser
        if (selectedAnswers.size === 0) {
            console.log('📡 Emitting empty selection for initialization');
        } else {
            console.log('📡 Emitting selection with data:', {
                selectedAnswers: [...selectedAnswers],
                selectedCertainties: Object.fromEntries(selectedCertainties),
                correctAnswers: correctAnswers
            });
        }
        
        const event = new CustomEvent('examQCMSelectionUpdate', {
            detail: {
                selectedAnswers: [...selectedAnswers],
                selectedCertainties: Object.fromEntries(selectedCertainties),
                correctAnswers: correctAnswers
            }
        });
        window.dispatchEvent(event);
    }, [selectedAnswers, selectedCertainties, correctAnswers]);

    if (!currentQCM || !currentQCM.qcm) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-400">{t('no_options_available')}</p>
            </div>
        );
    }

    const handleOptionSelect = (option, certainty) => {
        const correctAnswerTexts = new Set(correctAnswers.map(ca => ca.answer));
        
        if (correctAnswers.length > 1) {
            // Mode multi-réponses : ajouter à la sélection
            setSelectedAnswers(prev => {
                const newSet = new Set(prev);
                newSet.add(option.answer);
                return newSet;
            });
            
            // Stocker la certitude pour cette option spécifique
            setSelectedCertainties(prev => {
                const newMap = new Map(prev);
                newMap.set(option.answer, certainty);
                return newMap;
            });
            
            setDegreeOfCertainty(certainty);
            
            // Vérifier si toutes les bonnes réponses sont sélectionnées
            const newSelectedAnswers = new Set([...selectedAnswers, option.answer]);
            const allCorrectSelected = [...correctAnswerTexts].every(ca => newSelectedAnswers.has(ca));
            const allSelectedCorrect = [...newSelectedAnswers].every(answer => correctAnswerTexts.has(answer));
            
            console.log('🔍 Multi-response navigation check:', {
                newSelectedAnswers: [...newSelectedAnswers],
                correctAnswerTexts: [...correctAnswerTexts],
                allCorrectSelected,
                allSelectedCorrect,
                willNavigate: allCorrectSelected && allSelectedCorrect
            });
            
            if (allCorrectSelected && allSelectedCorrect) {
                // Toutes les bonnes réponses sont sélectionnées et aucune mauvaise réponse, soumettre
                console.log('✅ Navigating to solution UI - all correct answers selected and no incorrect answers');
                setTimeout(() => {
                    // Soumettre avec toutes les réponses sélectionnées
                    submitQCMAnswer([...newSelectedAnswers], certainty);
                    // Changer vers l'UI des bonnes réponses
                    setDisplayMode('solution');
                }, 100);
            } else {
                console.log('🔍 Multi-response selection status:', {
                    allCorrectSelected,
                    allSelectedCorrect,
                    willNavigate: false
                });
            }
        } else {
            // Mode simple réponse : comportement original
            setSelectedOption(option.answer);
            setDegreeOfCertainty(certainty);
            
            // Stocker la certitude pour cette option
            setSelectedCertainties(prev => {
                const newMap = new Map(prev);
                newMap.set(option.answer, certainty);
                return newMap;
            });
            
            // Auto-submit with selected certainty
            console.log('🔍 Single-response navigation check:', {
                selectedAnswer: option.answer,
                correctAnswers: correctAnswerTexts,
                isCorrect: correctAnswerTexts.has(option.answer),
                willNavigate: correctAnswerTexts.has(option.answer)
            });
            
            if (correctAnswerTexts.has(option.answer)) {
                console.log('✅ Correct answer selected - checking if all correct answers are selected');
                // Vérifier si toutes les bonnes réponses sont sélectionnées
                const newSelectedAnswers = new Set([...selectedAnswers, option.answer]);
                const allCorrectSelected = [...correctAnswerTexts].every(ca => newSelectedAnswers.has(ca));
                
                if (allCorrectSelected) {
                    console.log('✅ All correct answers selected - navigating to solution UI');
                    setTimeout(() => {
                        submitQCMAnswer(option.answer, certainty);
                        setDisplayMode('solution');
                    }, 100);
                } else {
                    console.log('✅ Some correct answers selected - continuing selection');
                    setTimeout(() => {
                        submitQCMAnswer(option.answer, certainty);
                    }, 100);
                }
            } else {
                // Réponse incorrecte, soumettre pour incrémenter attemptCount
                console.log('❌ Incorrect answer selected - submitting to increment attemptCount');
                setTimeout(() => {
                    submitQCMAnswer(option.answer, certainty);
                }, 100);
            }
        }
    };

    const handleToggleIncorrect = (optionIndex, isMarked) => {
        const option = allOptions[optionIndex];
        setMarkedIncorrect(prev => {
            const newSet = new Set(prev);
            if (isMarked) {
                newSet.add(option.answer);
            } else {
                newSet.delete(option.answer);
            }
            return newSet;
        });
    };

    const getCertaintyLabel = (level) => {
        switch (level) {
            case 'low': return t('low_certainty');
            case 'medium': return t('medium_certainty');
            case 'high': return t('high_certainty');
            default: return '';
        }
    };

    const getCertaintyColor = (level) => {
        switch (level) {
            case 'low': return 'text-red-400 bg-red-900/20 border-red-500/30';
            case 'medium': return 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30';
            case 'high': return 'text-green-400 bg-green-900/20 border-green-500/30';
            default: return 'text-gray-400 bg-gray-700 border-gray-600';
        }
    };

    return (
        <div className="space-y-4">
            {/* Score Display - Above question section, like funnel style */}
            {isEvaluated && rawScore && (
                <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-blue-500 shadow-lg mb-6">
                    <div className="flex items-center mb-4">
                        <div className="mr-3">
                            <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-blue-300">Score Evaluation</h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        {/* Raw Score */}
                        <div className="bg-gray-700 rounded-lg p-4">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-blue-400">{rawScore.raw}/10</div>
                                <div className="text-sm text-gray-400">Raw Score</div>
                                <div className="text-xs text-gray-500 mt-1">
                                    {rawScore.correctSelected}/{rawScore.totalCorrect} correct
                                </div>
                            </div>
                        </div>
                        
                        {/* Weighted Score */}
                        <div className="bg-gray-700 rounded-lg p-4">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-green-400">{rawScore.weighted}/10</div>
                                <div className="text-sm text-gray-400">Weighted Score</div>
                                <div className="text-xs text-gray-500 mt-1">
                                    (Same as raw for now)
                                </div>
                            </div>
                        </div>
                        
                        {/* Performance */}
                        <div className="bg-gray-700 rounded-lg p-4">
                            <div className="text-center">
                                <div className="text-2xl font-bold text-orange-400">
                                    {rawScore.raw >= validationThreshold ? 'Excellent' : rawScore.raw >= 6 ? 'Good' : rawScore.raw >= 4 ? 'Fair' : 'Needs Improvement'}
                                </div>
                                <div className="text-sm text-gray-400">Performance</div>
                                <div className="text-xs text-gray-500 mt-1">
                                    {rawScore.incorrectSelected > 0 ? `${rawScore.incorrectSelected} incorrect` : 'All correct'}
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Coefficients Details (Debug) */}
                    {rawScore.coefficients && (
                        <div className="mt-4">
                            <h4 className="text-md font-semibold text-gray-300 mb-2">
                                Score Calculation Details:
                            </h4>
                            <div className="bg-gray-700 rounded p-3 text-sm">
                                <div className="grid grid-cols-2 gap-2">
                                    <div>Base Score: {rawScore.coefficients.baseScore.toFixed(2)}</div>
                                    <div>Attempt Coeff: {rawScore.coefficients.attemptCoeff.toFixed(2)}</div>
                                    <div>Duration Coeff: {rawScore.coefficients.durationCoeff.toFixed(2)}</div>
                                    <div>Certainty Coeff: {rawScore.coefficients.certaintyCoeff.toFixed(2)}</div>
                                    <div className="col-span-2">Final: {rawScore.coefficients.final.toFixed(2)} × 10 = {rawScore.raw}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Weak Points */}
                    {weakPoints.length > 0 && (
                        <div className="mt-4">
                            <h4 className="text-md font-semibold text-red-300 mb-2">
                                Points to Review:
                            </h4>
                            <div className="space-y-2">
                                {weakPoints.map((point, index) => (
                                    <div key={index} className="bg-red-900/20 border border-red-500/30 rounded p-3">
                                        <div className="flex items-start">
                                            <svg className="w-4 h-4 text-red-400 mt-0.5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span className="text-red-300 text-sm">{point}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Question Reminder */}
            <div className="bg-gray-800 rounded-lg p-6 mb-6">
                <div className="text-lg leading-relaxed">
                    <SectionContent content={currentQCM.qcm.question} />
                </div>
                {correctAnswers.length > 1 && (
                    <div className="mt-4 p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                        <p className="text-blue-300 text-sm font-medium">
                            <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Multiple correct answers: Select all {correctAnswers.length} correct options ({selectedAnswers.size}/{correctAnswers.length} selected)
                        </p>
                    </div>
                )}
            </div>

            {/* Options */}
            <div className="space-y-4">
                {allOptions.map((option, index) => {
                    const correctAnswerTexts = new Set(correctAnswers.map(ca => ca.answer));
                    const isCorrect = correctAnswerTexts.has(option.answer);
                    const isMarkedIncorrect = markedIncorrect.has(option.answer);
                    const isSelected = correctAnswers.length > 1 ? selectedAnswers.has(option.answer) : selectedOption === option.answer;
                    
                    return (
                        <div
                            key={index}
                            className={`bg-gray-700 rounded-lg border transition-colors relative ${
                                isSelected && isCorrect
                                    ? 'border-green-500 bg-green-900/20'
                                    : isSelected && !isCorrect 
                                        ? 'border-red-500 bg-red-900/20' 
                                        : isMarkedIncorrect
                                            ? 'border-orange-500 bg-orange-900/20'
                                            : 'border-gray-600 hover:border-gray-500'
                            }`}
                            onMouseEnter={() => setHoveredOption(index)}
                            onMouseLeave={() => setHoveredOption(null)}
                        >
                        {/* Option Text */}
                        <div className="p-4 pr-12">
                            <div className="flex items-start min-w-0">
                                <p className="text-gray-200 leading-relaxed flex-grow min-w-0 break-words [overflow-wrap:anywhere] whitespace-pre-wrap">
                                    {option.answer}
                                </p>
                            </div>
                        </div>

                        {/* Toggle Button */}
                        <div className="absolute top-4 right-4">
                            <QCMOptionToggle
                                optionIndex={index}
                                onToggle={handleToggleIncorrect}
                                isMarked={isMarkedIncorrect}
                            />
                        </div>

                        {/* Certainty Levels - Horizontal gauges */}
                        <div className="p-3">
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => handleOptionSelect(option, 'low')}
                                    disabled={loading}
                                    className={`flex-1 h-2 bg-gray-600 rounded-full transition-all duration-200 hover:bg-red-500 ${selectedOption === option.answer && degreeOfCertainty === 'low' ? 'bg-red-500' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                                    title={t('low') || 'Faible'}
                                    onMouseEnter={() => setHoveredCertainty({ option: index, level: 'low' })}
                                    onMouseLeave={() => setHoveredCertainty({})}
                                />
                                <button
                                    onClick={() => handleOptionSelect(option, 'medium')}
                                    disabled={loading}
                                    className={`flex-1 h-2 bg-gray-600 rounded-full transition-all duration-200 hover:bg-yellow-500 ${selectedOption === option.answer && degreeOfCertainty === 'medium' ? 'bg-yellow-500' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                                    title={t('medium') || 'Moyen'}
                                    onMouseEnter={() => setHoveredCertainty({ option: index, level: 'medium' })}
                                    onMouseLeave={() => setHoveredCertainty({})}
                                />
                                <button
                                    onClick={() => handleOptionSelect(option, 'high')}
                                    disabled={loading}
                                    className={`flex-1 h-2 bg-gray-600 rounded-full transition-all duration-200 hover:bg-green-500 ${selectedOption === option.answer && degreeOfCertainty === 'high' ? 'bg-green-500' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
                                    title={t('high') || 'Élevé'}
                                    onMouseEnter={() => setHoveredCertainty({ option: index, level: 'high' })}
                                    onMouseLeave={() => setHoveredCertainty({})}
                                />
                            </div>
                        </div>

                        </div>
                    );
                })}
            </div>


            {/* Loading State */}
            {loading && (
                <div className="text-center py-4">
                    <div className="inline-flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-blue-400">{t('processing_answer')}</span>
                    </div>
                </div>
            )}

        </div>
    );
};

export default ExamQCMOptions;
